import { useState } from 'react';
import { ArrowLeft, Banknote, Calendar, CheckCircle, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { apiService } from '../services/api';
import type { Car } from './Listings';

type View = 'choice' | 'cash-form' | 'cash-success';

interface PurchaseOptionModalProps {
  open: boolean;
  onClose: () => void;
  onChooseFinancing: () => void;
  car: Car;
}

export function PurchaseOptionModal({
  open,
  onClose,
  onChooseFinancing,
  car,
}: PurchaseOptionModalProps) {
  const [view, setView] = useState<View>('choice');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [inquiryId, setInquiryId] = useState<number | null>(null);

  const msrp = car.totalPrice ?? car.monthlyPayment * car.termMonths;

  function handleClose() {
    // Reset state on close
    setView('choice');
    setName('');
    setEmail('');
    setPhone('');
    setError('');
    setInquiryId(null);
    onClose();
  }

  async function handleCashSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');

    if (!name.trim() || !email.trim() || !phone.trim()) {
      setError('All fields are required.');
      return;
    }

    setSubmitting(true);
    try {
      const result = await apiService.submitCashInquiry({
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        car_listing_id: car.id,
      });
      setInquiryId(result.id);
      setView('cash-success');
    } catch (err: any) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) handleClose(); }}>
      <DialogContent className="sm:max-w-lg">
        {view === 'choice' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-xl text-gray-900">
                How would you like to buy?
              </DialogTitle>
              <p className="text-sm text-gray-500 mt-1">
                {car.year} {car.make} {car.model}
              </p>
            </DialogHeader>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
              {/* Option 1 — Cash */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setView('cash-form');
                }}
                className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-gray-200 hover:border-[#f91bdc] hover:bg-[#f91bdc]/5 transition-all duration-200 text-left group"
              >
                <div className="w-12 h-12 rounded-full bg-[#f91bdc]/10 flex items-center justify-center group-hover:bg-[#f91bdc]/20 transition-colors">
                  <Banknote className="text-[#f91bdc]" size={24} />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-gray-900 text-base">Buy for Cash</p>
                  <p className="text-2xl font-bold text-[#f91bdc] mt-1">
                    ${msrp.toLocaleString()}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">One-time full payment (MSRP)</p>
                </div>
              </button>

              {/* Option 2 — Monthly installments */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onChooseFinancing();
                }}
                className="flex flex-col items-center gap-3 p-5 rounded-xl border-2 border-gray-200 hover:border-[#f91bdc] hover:bg-[#f91bdc]/5 transition-all duration-200 text-left group"
              >
                <div className="w-12 h-12 rounded-full bg-[#f91bdc]/10 flex items-center justify-center group-hover:bg-[#f91bdc]/20 transition-colors">
                  <Calendar className="text-[#f91bdc]" size={24} />
                </div>
                <div className="text-center">
                  <p className="font-semibold text-gray-900 text-base">Monthly Installments</p>
                  <p className="text-2xl font-bold text-[#f91bdc] mt-1">
                    ${car.monthlyPayment.toLocaleString()}<span className="text-sm font-normal text-gray-500">/mo</span>
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Over {car.termMonths} months — Shariah-compliant
                  </p>
                </div>
              </button>
            </div>
          </>
        )}

        {view === 'cash-form' && (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => { setError(''); setView('choice'); }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                  aria-label="Back"
                >
                  <ArrowLeft size={20} />
                </button>
                <DialogTitle className="text-xl text-gray-900">Cash Purchase Inquiry</DialogTitle>
              </div>
              <p className="text-sm text-gray-500 ml-7">
                {car.year} {car.make} {car.model} — <span className="font-semibold text-[#f91bdc]">${msrp.toLocaleString()}</span>
              </p>
            </DialogHeader>

            <p className="text-sm text-gray-600 mt-2">
              Leave your contact details and our team will reach out within 1 business day to finalize the purchase.
            </p>

            <form onSubmit={handleCashSubmit} className="space-y-4 mt-4">
              <div className="space-y-1">
                <Label htmlFor="cash-name">Full Name</Label>
                <Input
                  id="cash-name"
                  type="text"
                  placeholder="John Smith"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="cash-email">Email Address</Label>
                <Input
                  id="cash-email"
                  type="email"
                  placeholder="john@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="cash-phone">Phone Number</Label>
                <Input
                  id="cash-phone"
                  type="tel"
                  placeholder="+1 (555) 000-0000"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  required
                />
              </div>

              {error && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
                  {error}
                </p>
              )}

              <Button
                type="submit"
                disabled={submitting}
                className="w-full bg-[#f91bdc] hover:bg-[#e015c9] text-white py-3"
              >
                {submitting ? (
                  <span className="flex items-center gap-2">
                    <Loader2 size={16} className="animate-spin" />
                    Submitting…
                  </span>
                ) : (
                  'Submit Inquiry'
                )}
              </Button>
            </form>
          </>
        )}

        {view === 'cash-success' && (
          <div className="flex flex-col items-center text-center py-4 gap-4">
            <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center">
              <CheckCircle className="text-green-500" size={36} />
            </div>
            <DialogTitle className="text-xl text-gray-900">Inquiry Received!</DialogTitle>
            <p className="text-gray-600 text-sm max-w-xs">
              Thank you, <strong>{name}</strong>! We've received your cash purchase inquiry for the{' '}
              <strong>{car.year} {car.make} {car.model}</strong>. A confirmation has been sent to{' '}
              <strong>{email}</strong> and our team will contact you within 1 business day.
            </p>
            {inquiryId && (
              <p className="text-xs text-gray-400">Inquiry ID: #{inquiryId}</p>
            )}
            <Button
              onClick={handleClose}
              className="mt-2 bg-[#f91bdc] hover:bg-[#e015c9] text-white px-8"
            >
              Close
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
